import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import {
  classifyStreamError,
  drmServers,
  drmSupported,
  STREAM_FAILURE_MESSAGE,
  type StreamFailure,
} from "../schemes/_shared/streamPlayback";
import { bridge, deviceModel, devicePlatform } from "../lib/foyerBridge";
import { hostParams } from "../lib/hostQuery";
import { installProbeChord } from "../lib/probeChord";
import {
  DEFAULT_PROBE_PLAN,
  diagnoseDrm,
  parseProbePlan,
  runPlayerProbe,
  scrubSecrets,
  summarise,
  WIDEVINE_ROBUSTNESS,
  type EmeSnapshot,
  type PlanSource,
  type PlayOutcome,
  type PlayRequest,
  type ProbePlan,
  type ProbeReport,
  type ProbeRunner,
  type SecurityLevel,
} from "./playerProbe";

/**
 * `?probe=player` - the on-device player probe (PLAYER-2 / PLAYER-4).
 *
 * `docs/player-hardware-tests.md` describes two tests that cost about twenty
 * minutes each of a person's time at a panel: place a protected title over the
 * API, schedule it, watch a wall for two minutes, pull a log capture, read a
 * five-row table, then walk a five-clip ladder and read a second one. This page
 * is those tests, run by the panel, printed as the table the runbook asks
 * somebody to assemble by hand.
 *
 * ## The rules it inherits from `?probe=1` (`components/PlatformProbe.tsx`)
 *
 *  1. **NOTHING MAY THROW.** This runs on hardware whose media stack we are
 *     guessing at. A probe that white-screens because `shaka-player` would not
 *     import has destroyed the measurement it exists to take.
 *  2. **NO MODERN CSS.** The page must render on an engine older than the floor
 *     we hope to find - a blank wall on a Tizen 5 panel teaches nothing, when
 *     what we needed to learn was "this is Tizen 5". Margins and pixels only.
 *  3. **NO SECRETS.** Licence URLs are ES256 JWTs in a query string. Every URL
 *     printed here goes through `redactLicenceUrl`, and every free-text detail
 *     through `scrubSecrets`.
 *
 * ## And two of its own
 *
 *  4. **ONE SURFACE AT A TIME.** A hotel panel's hardware H.264 decoders are a
 *     pool of single digits, and an Android WebView does NOT release one when
 *     the `<video>` leaves the DOM ([[panel-video-encoding]]). So there is
 *     exactly one `<video>` element on this page for the whole run, reused, and
 *     released explicitly between every attempt: `pause()` →
 *     `removeAttribute("src")` → `load()` → `player.destroy()`. Suite C then
 *     does that fifty times and asserts nothing leaked.
 *  5. **OUT OF THE GUEST'S WAY.** Reachable only by typing `?probe=player`.
 *     It appears in no menu, is linked from nowhere, registers nothing, and -
 *     because `commitGoodShell()` is only ever called by the launcher - can
 *     never become the offline shell a dark room boots into.
 *  6. **HAS A WAY BACK.** 2026-09-08, hasselbacken/302: this page was reached
 *     by pointing an installed kiosk's boot URL at `?probe=player` (there is
 *     no way to open it as a live overlay on a device with no address bar,
 *     unlike `?probe=1`), and it had none - a bench panel sat on this page,
 *     off the fleet, until someone drove out an ADB-signed rebuild to revert
 *     the boot URL. It now arms the SAME chord `?probe=1` uses to OPEN
 *     ([[probeChord]]) to instead LEAVE: there is no ambiguity, because
 *     `?probe=player` and `?probe=1` are mutually exclusive routes decided
 *     before either ever mounts.
 */

/** How long a single `load()` may hang before the attempt is abandoned. */
const LOAD_TIMEOUT_MS = 25_000;

/** How often the playhead is sampled while an attempt is being watched. */
const SAMPLE_MS = 500;

/** A six-minute hold, which outlives a five-minute Verimatrix token. */
const EXPIRY_WATCH_MS = 6 * 60_000;

// ---------------------------------------------------------------------------
// The real runner: the only part of the probe that knows what a decoder is
// ---------------------------------------------------------------------------

interface ShakaPlayer {
  attach(media: HTMLMediaElement): Promise<void>;
  load(uri: string, startTime?: number): Promise<void>;
  destroy(): Promise<void>;
  configure(config: unknown): void;
  addEventListener(type: string, cb: (e: unknown) => void): void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Reject rather than hang forever: a panel that never settles is a finding, and
 * a `load()` that never resolves would otherwise take every remaining rung with
 * it. The timer is cleared on settle so a completed run leaves nothing pending.
 */
function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    p,
    new Promise<T>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`${what} did not settle in ${ms}ms`)),
        ms
      );
    }),
  ]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/** A short, non-throwing description. Scrubbed; never shown to a guest. */
function describe(e: unknown): string {
  if (e && typeof e === "object") {
    const err = e as { code?: number; category?: number; message?: string };
    if (typeof err.code === "number") {
      return `shaka ${err.category ?? "?"}.${err.code}`;
    }
    if (err.message) return scrubSecrets(err.message.slice(0, 160));
  }
  return scrubSecrets(String(e).slice(0, 160));
}

/**
 * Which of the surfaces Shaka's polyfills patch exist RIGHT NOW.
 *
 * Snapshotted either side of `installAll()` so "is the polyfill still required
 * on this device" becomes a measurement instead of an assumption. Never throws:
 * a getter that explodes counts as absent, which is the conservative answer.
 */
function emeSnapshot(): EmeSnapshot {
  const has = (fn: () => unknown): boolean => {
    try {
      const v = fn();
      return v !== undefined && v !== null;
    } catch {
      return false;
    }
  };
  const w = window as unknown as Record<string, unknown>;
  return {
    "navigator.requestMediaKeySystemAccess": has(
      () => navigator.requestMediaKeySystemAccess
    ),
    "window.MediaSource": has(() => w.MediaSource),
    "window.MediaKeys": has(() => w.MediaKeys),
    "window.MediaKeySystemAccess": has(() => w.MediaKeySystemAccess),
    "HTMLMediaElement.setMediaKeys": has(
      () =>
        (w.HTMLMediaElement as { prototype?: Record<string, unknown> })
          ?.prototype?.setMediaKeys
    ),
    "window.VTTCue": has(() => w.VTTCue),
    "navigator.mediaCapabilities": has(
      () => (navigator as unknown as Record<string, unknown>).mediaCapabilities
    ),
  };
}

/**
 * Ask the CDM how strong a Widevine it will give this panel.
 *
 * There is no API that reports "L1". There is a robustness the CDM either grants
 * or refuses, so we ask down the ladder strongest-first and report the first
 * yes. A capability probe, exactly like `drmSupported()` - never a user-agent
 * guess, per the FoyerBridge rule. A panel that grants none of them answers
 * `unknown`, and unknown is printed as unknown.
 */
async function probeSecurityLevel(): Promise<{
  level: SecurityLevel;
  robustness: string | null;
}> {
  const nav =
    typeof navigator === "undefined"
      ? undefined
      : (navigator as unknown as {
          requestMediaKeySystemAccess?: (
            keySystem: string,
            configs: unknown[]
          ) => Promise<unknown>;
        });
  if (typeof nav?.requestMediaKeySystemAccess !== "function") {
    return { level: "unknown", robustness: null };
  }
  for (const step of WIDEVINE_ROBUSTNESS) {
    try {
      await nav.requestMediaKeySystemAccess("com.widevine.alpha", [
        {
          initDataTypes: ["cenc"],
          videoCapabilities: [
            {
              contentType: 'video/mp4; codecs="avc1.42E01E"',
              robustness: step.robustness,
            },
          ],
        },
      ]);
      return { level: step.level, robustness: step.robustness };
    } catch {
      // Refused at this strength - ask for the next one down.
    }
  }
  return { level: "unknown", robustness: null };
}

/**
 * Bind a runner to the one `<video>` element this page owns.
 *
 * Every attempt creates a Shaka player, loads, watches, and hands everything
 * back. The `finally` is the important half: all four release steps, every time,
 * including on the paths where the load threw - because Suite C is about to do
 * this fifty times in a row and a leak anywhere makes the rest of the run lie.
 */
export function createShakaRunner(video: HTMLVideoElement): ProbeRunner {
  return {
    emeAvailable: () =>
      typeof navigator !== "undefined" &&
      typeof navigator.requestMediaKeySystemAccess === "function",
    widevineSupported: () => drmSupported(),
    securityLevel: probeSecurityLevel,
    heapBytes: () => {
      const mem = (
        performance as unknown as { memory?: { usedJSHeapSize?: number } }
      ).memory;
      return typeof mem?.usedJSHeapSize === "number"
        ? mem.usedJSHeapSize
        : null;
    },
    async play(req: PlayRequest): Promise<PlayOutcome> {
      // FORGET THE PREVIOUS ATTEMPT'S PLAYHEAD FIRST.
      //
      // Both caches are keyed on the one reused `<video>`, and the parked
      // position is read after the element has been released (so it is 0 / NaN
      // and always falls back to the cache). Carrying the last rung's numbers
      // into an attempt that never sampled would INVERT the tell that cost a
      // day: a rung which never decoded would inherit "parked at the duration"
      // and be reported as a loop failure - and `readLadder` counts a loop
      // failure as neither a pass nor a failure, so the ceiling would silently
      // skip the real one.
      lastSeen.delete(video);
      lastDuration.delete(video);
      const before = emeSnapshot();
      // Left undefined until `installAll()` has actually RUN. A dynamic import
      // that rejects on an old WebView would otherwise leave before === after
      // and print "the polyfill is not required on this device" - inverted
      // evidence, gathered from a panel where Shaka never ran at all.
      let after: EmeSnapshot | undefined;
      let player: ShakaPlayer | null = null;
      let startupMs: number | null = null;
      let played = false;
      let sustained = false;
      const startedAt = Date.now();
      // Held on an object rather than in a `let`, because the error listener
      // below writes it from a callback and the reads happen in this function -
      // a shape TypeScript cannot narrow, and one where being wrong would
      // silently drop the code that IS the finding.
      const fault: { failure: StreamFailure | null; detail: string } = {
        failure: null,
        detail: "",
      };

      const note = (f: StreamFailure, d: string) => {
        // FIRST failure wins. A load that fails often emits several errors as
        // the pipeline tears down, and the later ones describe the teardown
        // rather than the cause.
        if (fault.failure) return;
        fault.failure = f;
        fault.detail = d;
      };

      try {
        const mod = await import("shaka-player/dist/shaka-player.compiled");
        const lib = (mod as { default?: unknown }).default ?? mod;
        const api = lib as {
          polyfill: { installAll(): void };
          Player: new () => ShakaPlayer;
        };
        // Idempotent. Called here for the same reason StreamSurface calls it,
        // and snapshotted either side so the probe can say whether it was still
        // NEEDED on this device.
        api.polyfill.installAll();
        after = emeSnapshot();

        const instance = new api.Player();
        player = instance;
        await instance.attach(video);

        // A withheld licence is `licenceUrl: null`, and configuring nothing is
        // exactly what "withheld" means: the CDM gets no server to challenge.
        // The expected outcome is a refusal, and a success is the finding.
        if (req.licenceUrl) {
          const servers = drmServers({ widevineUrl: req.licenceUrl });
          if (Object.keys(servers).length > 0) {
            instance.configure({ drm: { servers } });
          }
        }

        instance.addEventListener("error", (e) => {
          const d = (e as { detail?: unknown }).detail;
          note(classifyStreamError(d), describe(d));
        });

        await withTimeout(instance.load(req.url), LOAD_TIMEOUT_MS, "load()");

        video.muted = true;
        try {
          await video.play();
        } catch {
          // Autoplay policy or a panel that will not start: the sampling below
          // reports a playhead parked at 0, which is the honest result.
        }

        const samples: number[] = [];
        const deadline = Date.now() + Math.max(req.watchMs, SAMPLE_MS);
        while (Date.now() < deadline) {
          await sleep(Math.min(SAMPLE_MS, Math.max(50, req.watchMs)));
          const t = safeTime(video);
          if (!played && t > 0) {
            played = true;
            startupMs = Date.now() - startedAt;
          }
          samples.push(t);
          // Remember the duration WHILE the element still has one: releasing it
          // leaves `duration` NaN, and "parked at the duration" is the tell that
          // separates a loop failure from a decode refusal.
          safeDuration(video);
          if (fault.failure) break;
        }
        const last = samples[samples.length - 1] ?? 0;
        const prev = samples[samples.length - 2];
        // "Sustained" is the playhead STILL MOVING when the window closed, not
        // "it started". A clip that plays five seconds and stalls is the exact
        // failure `panel-video-encoding` measured, and a first-frame check would
        // have called it a pass.
        sustained =
          fault.failure === null &&
          played &&
          (prev === undefined ? last > 0 : last > prev);
      } catch (e) {
        note(classifyStreamError(e), describe(e));
      } finally {
        // ALL FOUR STEPS, ALWAYS. `destroy()` alone leaves the element holding a
        // source on some WebViews, and the element alone leaves Shaka's buffers
        // allocated. Cleanup may never throw - a throw here would abandon every
        // remaining rung.
        try {
          await player?.destroy();
        } catch {
          /* nothing to do but keep going */
        }
        try {
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch {
          /* cleanup must never throw */
        }
      }

      return {
        played,
        sustained,
        startupMs,
        parkedAtSeconds: safeTime(video, true),
        durationSeconds: safeDuration(video),
        failure: fault.failure,
        detail: fault.detail,
        emeBefore: before,
        emeAfter: after,
      };
    },
  };
}

/**
 * The playhead, read without throwing.
 *
 * `parked` is read AFTER the release above has run, which on most engines
 * leaves `currentTime` at its last value and on some resets it to 0 - so the
 * last SAMPLE is preferred when the element has already been torn down. The
 * caller keeps its samples; this is the fallback.
 */
function safeTime(video: HTMLVideoElement, afterRelease = false): number {
  try {
    const t = video.currentTime;
    if (!Number.isFinite(t)) return 0;
    if (afterRelease && t === 0) return lastSeen.get(video) ?? 0;
    lastSeen.set(video, t);
    return t;
  } catch {
    return 0;
  }
}

/**
 * The last non-zero playhead seen per element.
 *
 * The whole value of Suite B is WHERE the playhead parked (0.1s = a decode
 * refusal, the duration = a loop failure), and releasing the element can wipe
 * `currentTime` before we read it. Remembering the last sample is what keeps
 * that distinction, which is the tell that cost a day.
 */
const lastSeen = new WeakMap<HTMLVideoElement, number>();

/**
 * The clip's duration, remembered across the release.
 *
 * Same reasoning as {@link lastSeen}: `removeAttribute("src")` + `load()` leaves
 * `duration` NaN, and the parked-vs-duration comparison is the whole tell.
 */
function safeDuration(video: HTMLVideoElement): number | null {
  try {
    const d = video.duration;
    if (Number.isFinite(d) && d > 0) {
      lastDuration.set(video, d);
      return d;
    }
    return lastDuration.get(video) ?? null;
  } catch {
    return null;
  }
}

const lastDuration = new WeakMap<HTMLVideoElement, number>();

// ---------------------------------------------------------------------------
// The plan
// ---------------------------------------------------------------------------

export interface ResolvedPlan {
  plan: ProbePlan;
  source: PlanSource;
}

/**
 * Resolve the plan, tolerantly, and let the URL override it.
 *
 * ## 🚨 A plan is fetched only when one was ASKED for
 *
 * This used to fetch `/api/foyer/player-probe/plan` on every run, "a sibling
 * unit owns the route". That unit shipped and built something else: the server
 * route is `GET /screens/:screenId/tv/probe-plan?assetId=…`, it wants a SCREEN
 * TOKEN, and it wants a catalogue title to read a ladder off. This probe is
 * defined by running with no premise, no pairing and no screen — so it could
 * never call that route, and the global one it was waiting for was never built
 * and has no reason to be.
 *
 * What the unconditional fetch actually did on a panel: one request per run to
 * a path the SPA fallback answers with `index.html`, so `res.ok` was true,
 * `res.json()` threw, and the throw was swallowed. Harmless, invisible, and a
 * request that can hang for the fetch timeout on a hotel network before the
 * first suite starts.
 *
 * So `&plan=<url>` is now the only way to a server plan — which is also how you
 * reach the real route, with a screen token in the URL — and nothing is fetched
 * without it.
 *
 * ## And a plan that was asked for and did not load SAYS SO
 *
 * Falling back to the built-in ladder stays right: a diagnostic that only works
 * once the rest of the system is finished is not a diagnostic. But once the
 * fetch is opt-in, a failed one is somebody's explicit request failing, and
 * reporting that identically to "no plan was requested" hides the one fact that
 * invalidates the run — a mistyped `&plan=` produces a full, plausible, wrong
 * report. `plan_unreachable` is that distinction.
 */
export async function resolvePlan(
  params: URLSearchParams,
  fetchImpl: typeof fetch = fetch
): Promise<ResolvedPlan> {
  let plan = DEFAULT_PROBE_PLAN;
  let source: ResolvedPlan["source"] = "default";
  const planUrl = params.get("plan");
  if (planUrl) {
    try {
      const res = await fetchImpl(planUrl, {
        headers: { accept: "application/json" },
      });
      if (!res.ok) throw new Error(`plan route answered ${res.status}`);
      plan = parseProbePlan(await res.json());
      source = "server";
    } catch {
      // A 404, an HTML error page, a shape nobody agreed on, or no network.
      // The built-in ladder stands and the screen says it was a fallback.
      source = "plan_unreachable";
    }
  }

  const drmUrl = params.get("drm");
  const licenceUrl = params.get("licence");
  const cycles = Number.parseInt(params.get("cycles") ?? "", 10);
  const expiry = params.get("expiry") === "1";
  if (drmUrl || licenceUrl || Number.isFinite(cycles) || expiry) {
    plan = {
      ...plan,
      drm: {
        ...plan.drm,
        manifestUrl: drmUrl || plan.drm.manifestUrl,
        licenceUrl: licenceUrl || plan.drm.licenceUrl,
        label: drmUrl ? "supplied by ?drm=" : plan.drm.label,
        expiryWatchMs: expiry ? EXPIRY_WATCH_MS : plan.drm.expiryWatchMs,
      },
      soak: {
        ...plan.soak,
        cycles:
          Number.isFinite(cycles) && cycles > 0 ? cycles : plan.soak.cycles,
      },
    };
    // A failed explicit plan is the more alarming of the two facts and must
    // survive a switch that only touched the DRM row and the soak length.
    source = source === "plan_unreachable" ? source : "override";
  }
  return { plan, source };
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export interface PlayerProbeProps {
  /** Injected by the tests. Production binds one to the `<video>` below. */
  runner?: ProbeRunner;
  /** Injected by the tests, skipping the plan fetch entirely. */
  plan?: ResolvedPlan;
}

export default function PlayerProbe({ runner, plan }: PlayerProbeProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [report, setReport] = useState<ProbeReport | null>(null);

  // Scale the type to the panel: this page is read off a PHOTOGRAPH taken at a
  // television, so a size chosen for a laptop wastes the trip. Computed in JS
  // rather than with `clamp()`, per rule 2 in the header.
  const baseFont = Math.max(
    14,
    Math.min(22, Math.round((window.innerWidth || 1280) / 96))
  );

  // The escape hatch. See rule 6 above: this page can be the ONLY thing an
  // installed app ever loads (a boot-URL override, not an overlay), so
  // leaving it means a hard navigation, not a state change. Reuses the exact
  // chord `?probe=1` uses to open, because on this page it can only ever mean
  // "let me out" - never "open the overlay", which is a different route.
  useEffect(() => {
    installProbeChord(() => {
      try {
        const url = new URL(window.location.href);
        for (const p of [
          "probe",
          "drm",
          "licence",
          "cycles",
          "expiry",
          "plan",
        ]) {
          url.searchParams.delete(p);
        }
        url.searchParams.set("tv", "1");
        window.location.href = url.toString();
      } catch {
        // An escape hatch that can throw is not one. Fall back to the one
        // URL every platform's kiosk shell is known to boot to.
        window.location.href = "https://foyer.nenda.com/?tv=1";
      }
    });
  }, []);

  useEffect(() => {
    // NO `hasRun` REF HERE, on purpose, and it was wrong once.
    //
    // StrictMode (dev, `main.tsx`) mounts → effect → cleanup → effect. A ref
    // that latched "already started" on the first pass, combined with the
    // `cancelled` guard the first pass's cleanup sets, cancelled the probe
    // twice over: pass 1 aborted at the guard, pass 2 refused to start, and
    // `?probe=player` sat on "starting…" forever in dev.
    //
    // The cancellation flag alone is enough, because the plan resolution below
    // always crosses a microtask boundary before anything acquires a decoder -
    // so the abandoned pass gives up before it can fight the live one for the
    // single `<video>` element.
    let cancelled = false;

    void (async () => {
      try {
        const resolved = await Promise.resolve(
          plan ?? resolvePlan(hostParams())
        );
        // Nothing below may acquire a decoder after this page has gone away -
        // that is precisely how the leak this probe measures gets created.
        if (cancelled) return;
        const video = videoRef.current;
        const active = runner ?? (video ? createShakaRunner(video) : null);
        if (!active) return;
        const out = await runPlayerProbe({
          plan: resolved.plan,
          planSource: resolved.source,
          runner: active,
          device: describeDevice(),
          is4k: is4kPanel(),
          onProgress: (r) => {
            if (cancelled) return;
            setReport(r);
            // The whole point of the log line: every Tizen finding so far was
            // made from a desk, through `log_capture`, which uploads the console
            // ring buffer. Warn level because the Android shell forwards only
            // warnings and errors to logcat.
            console.warn(`[player-probe] ${r.phase}`);
          },
        });
        if (cancelled) return;
        setReport(out);
        console.warn("[player-probe] report:", JSON.stringify(out));
        for (const line of summarise(out)) {
          console.warn(`[player-probe] ${line}`);
        }
      } catch (err) {
        // A probe that white-screens has destroyed its own measurement.
        console.warn("[player-probe] run failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runner, plan]);

  const drm = report?.drm;
  const observed = drm?.attempts.find((a) => a.id === "licensed");

  return (
    <div style={{ ...S.page, fontSize: baseFont }} data-testid="player-probe">
      <div style={S.header}>
        <span style={S.title}>
          FOYER PLAYER PROBE &mdash; PLAYER-2 / PLAYER-4
        </span>
        <span style={S.headerRight}>
          {String(describeDevice().model)} &middot;{" "}
          {report ? report.phase : "starting…"}
        </span>
      </div>

      <div style={S.bannerDead}>
        STANDALONE (?probe=player) &mdash; the app is NOT running: this device
        is offline to the fleet and will ignore CMS commands until you leave
        this page. Let it finish, then photograph the VERDICT block below. To
        leave: Up Up Down Down Left Right on the remote.
      </div>

      {/* The finding, in the words the runbook asks somebody to write down.
          First fold on purpose: a photograph of the top of this page is already
          the answer to both spikes. */}
      <div style={S.verdict} data-testid="player-probe-verdict">
        <div style={S.sectionTitle}>VERDICT</div>
        {report ? (
          summarise(report).map((line) => (
            <div key={line} style={S.verdictLine}>
              {line}
            </div>
          ))
        ) : (
          <div style={S.hint}>running…</div>
        )}
      </div>

      <div style={S.cols}>
        <div style={S.colWide}>
          {/* ---- SUITE A ------------------------------------------------- */}
          <div style={S.section}>
            <div style={S.sectionTitle}>
              SUITE A &mdash; can this panel open encrypted DASH? (PLAYER-2)
            </div>
            <Row k="EME available" v={yesNo(drm?.emeAvailable)} />
            <Row k="Widevine answers" v={yesNo(drm?.widevineSupported)} />
            <Row
              k="Widevine level"
              v={`${drm?.securityLevel ?? "—"}${
                drm?.robustness ? ` (${drm.robustness})` : ""
              }`}
            />
            <Note text={drm?.securityNote} />
            <Row
              k="polyfill.installAll() required"
              v={
                drm?.polyfill
                  ? drm.polyfill.required
                    ? "YES"
                    : "no"
                  : "not measured"
              }
            />
            <Note text={drm?.polyfill?.note} />
            <Row k="licence expiry mid-play" v="" />
            <Note text={drm?.expiry} />

            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>attempt</th>
                  <th style={S.th}>played</th>
                  <th style={S.th}>code</th>
                  <th style={S.th}>startup</th>
                  <th style={S.th}>parked</th>
                  <th style={S.th}>licence</th>
                </tr>
              </thead>
              <tbody>
                {(drm?.attempts ?? []).map((a) => (
                  <tr key={a.id} data-testid={`drm-attempt-${a.id}`}>
                    <td style={S.td}>{a.label}</td>
                    <td style={a.played ? S.tdYes : S.tdNo}>
                      {a.played ? "yes" : "no"}
                    </td>
                    <td style={S.td}>
                      {a.failure ?? "—"}
                      {a.detail ? ` · ${a.detail}` : ""}
                      {a.failure ? (
                        <div style={S.guest}>
                          on screen: “{STREAM_FAILURE_MESSAGE[a.failure]}”
                        </div>
                      ) : null}
                    </td>
                    <td style={S.td}>
                      {a.startupMs === null ? "—" : `${a.startupMs} ms`}
                    </td>
                    <td style={S.td}>
                      {a.parkedAtSeconds === null
                        ? "—"
                        : `${a.parkedAtSeconds.toFixed(1)}s`}
                    </td>
                    <td style={S.td}>{a.licence}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {drm?.withheld ? (
              <div
                style={drm.withheld.pass ? S.bannerLive : S.bannerDead}
                data-testid="withheld-verdict"
              >
                LICENCE WITHHELD: {drm.withheld.pass ? "PASS" : "FAIL"} &mdash;{" "}
                {drm.withheld.note}
              </div>
            ) : null}
          </div>

          {/* ---- the five ways a black screen happens --------------------- */}
          <div style={S.section}>
            <div style={S.sectionTitle}>
              WHAT A BLACK SCREEN MEANT &mdash; keyed by the failure code
            </div>
            <div style={S.warn}>
              ⚠️ Do not read “it did not play” as “no DRM”. Four of these rows
              are not about DRM at all, and the code is what separates them.
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>code</th>
                  <th style={S.th}>what it means</th>
                  <th style={S.th}>next</th>
                </tr>
              </thead>
              <tbody>
                {BLACK_SCREEN_ROWS.map((row) => {
                  const hit =
                    observed !== undefined &&
                    (row.code === null
                      ? observed.played
                      : !observed.played && observed.failure === row.code);
                  const d = diagnoseDrm(row.code === null, row.code);
                  return (
                    <tr
                      key={String(row.code)}
                      style={hit ? S.trHit : undefined}
                      data-testid={hit ? "black-screen-observed" : undefined}
                    >
                      <td style={S.td}>
                        {hit ? "▶ " : ""}
                        {row.code ?? "it plays"}
                      </td>
                      <td style={S.td}>{d.meaning}</td>
                      <td style={S.td}>{d.next}</td>
                    </tr>
                  );
                })}
                <tr
                  style={
                    observed && !observed.played && observed.failure === null
                      ? S.trHit
                      : undefined
                  }
                >
                  <td style={S.td}>no code at all</td>
                  <td style={S.td}>The player never started</td>
                  <td style={S.td}>
                    Check the screen is on the new bundle (Devices → Web bundle)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ---- SUITE B ------------------------------------------------- */}
          <div style={S.section}>
            <div style={S.sectionTitle}>
              SUITE B &mdash; the decode ladder (PLAYER-4)
            </div>
            <div style={S.warn}>
              ⚠️ A clip that never decodes and a clip that will not loop look
              IDENTICAL on a wall. The difference is where the playhead parked:
              ~0.0–0.1s is a decode refusal, the duration is a loop failure.
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>rung</th>
                  <th style={S.th}>outcome</th>
                  <th style={S.th}>parked / duration</th>
                  <th style={S.th}>startup</th>
                  <th style={S.th}>code</th>
                  <th style={S.th}>what it means</th>
                </tr>
              </thead>
              <tbody>
                {(report?.ladder ?? []).map((r) => (
                  <tr key={r.id} data-testid={`rung-${r.id}`}>
                    <td style={S.td}>{r.label}</td>
                    <td
                      style={r.outcome === "plays" ? S.tdYes : S.tdNo}
                      data-testid={`rung-${r.id}-outcome`}
                    >
                      {r.outcome}
                    </td>
                    <td style={S.td}>
                      {r.parkedAtSeconds === null
                        ? "—"
                        : `${r.parkedAtSeconds.toFixed(1)}s`}
                      {" / "}
                      {r.durationSeconds === null
                        ? "—"
                        : `${r.durationSeconds.toFixed(1)}s`}
                    </td>
                    <td style={S.td}>
                      {r.startupMs === null ? "—" : `${r.startupMs} ms`}
                    </td>
                    <td style={S.td}>
                      {r.failure ?? "—"}
                      {r.detail ? ` · ${r.detail}` : ""}
                    </td>
                    <td style={S.td}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report?.ceiling ? (
              <div style={S.bannerNeutral} data-testid="ceiling-verdict">
                {report.ceiling.meaning}
                {report.ceiling.abrCapMbps !== null
                  ? ` — set the ABR cap at or below ${report.ceiling.abrCapMbps} Mbps.`
                  : ""}
              </div>
            ) : null}
          </div>

          {/* ---- SUITE C ------------------------------------------------- */}
          <div style={S.section}>
            <div style={S.sectionTitle}>
              SUITE C &mdash; the decoder-release soak
            </div>
            {report?.soak ? (
              <>
                <Row
                  k="cycles"
                  v={`${report.soak.cyclesCompleted} / ${report.soak.cyclesRequested}`}
                />
                <Row
                  k="decoder_exhausted"
                  v={String(report.soak.decoderExhausted)}
                />
                <div
                  style={report.soak.pass ? S.bannerLive : S.bannerDead}
                  data-testid="soak-verdict"
                >
                  {report.soak.pass ? "PASS" : "FAIL"} &mdash;{" "}
                  {report.soak.note}
                </div>
              </>
            ) : (
              <div style={S.hint}>not run yet…</div>
            )}
          </div>
        </div>

        <div style={S.colNarrow}>
          <div style={S.section}>
            <div style={S.sectionTitle}>DEVICE &mdash; write this down</div>
            {Object.entries(describeDevice()).map(([k, v]) => (
              <Row key={k} k={k} v={String(v ?? "—")} />
            ))}
            <Row k="plan" v={report?.plan.source ?? "resolving…"} />
            <Row k="DRM asset" v={report?.plan.drmAsset ?? "—"} />
          </div>

          {/* Copyable, because a photograph is the fallback and a paste is
              better. Redacted before it ever reaches here. */}
          <div style={S.section}>
            <div style={S.sectionTitle}>JSON &mdash; select all &amp; copy</div>
            <textarea
              readOnly
              style={S.json}
              data-testid="player-probe-json"
              value={report ? JSON.stringify(report, null, 1) : "{}"}
            />
          </div>

          {/* ONE element, for the whole run. See rule 4 in the header. */}
          <div style={S.section}>
            <div style={S.sectionTitle}>THE ONE SURFACE</div>
            <video
              ref={videoRef}
              muted
              playsInline
              style={S.video}
              data-testid="player-probe-video"
            />
          </div>
        </div>
      </div>

      <div style={S.footer}>
        ?probe=player &nbsp;|&nbsp; &amp;drm=&lt;manifest&gt;
        &amp;licence=&lt;url&gt; to point it at a real title &nbsp;|&nbsp;
        &amp;expiry=1 adds a 6-minute mid-play expiry hold &nbsp;|&nbsp;
        &amp;cycles=&lt;n&gt; changes the soak &nbsp;|&nbsp;
        &amp;plan=&lt;url&gt; for a server-supplied ladder
      </div>
    </div>
  );
}

/** The documented rows, in the runbook's order. `null` = "it plays". */
const BLACK_SCREEN_ROWS: Array<{ code: StreamFailure | null }> = [
  { code: null },
  { code: "licence_refused" },
  { code: "codec_unsupported" },
  { code: "manifest_unreachable" },
  { code: "licence_expired" },
  { code: "decoder_exhausted" },
  { code: "network_stalled" },
  { code: "unknown" },
];

function yesNo(v: boolean | undefined): string {
  return v === undefined ? "—" : v ? "yes" : "NO";
}

/**
 * The facts the runbook insists on: "it worked on a MediaSuite" is not a
 * finding; "it worked on a 43HFL5214U/12 on firmware X" is.
 *
 * Never throws, and never reads a token: every value here is a fact about the
 * hardware or the browser.
 */
function describeDevice(): Record<string, string | number | undefined> {
  const safe = <T,>(fn: () => T): T | undefined => {
    try {
      return fn();
    } catch {
      return undefined;
    }
  };
  return {
    model: safe(() => deviceModel()) ?? "unknown",
    platform: safe(() => devicePlatform()) ?? "unknown",
    firmware: safe(() => bridge.getDeviceInfo()?.firmware) ?? "unknown",
    appVersion:
      safe(() => bridge.appVersion() ?? undefined) ??
      (import.meta.env?.VITE_APP_VERSION as string | undefined) ??
      "dev",
    screen: safe(() => `${window.screen.width}×${window.screen.height}`),
    devicePixelRatio: safe(() => window.devicePixelRatio),
    userAgent: safe(() => navigator.userAgent),
  };
}

/**
 * Does this panel actually claim a 4K framebuffer?
 *
 * Asked of the device, not of a model string. A 4K rung run on a 1080 panel
 * measures the scaler, not the decoder, and would be reported as a ceiling that
 * is not there.
 */
function is4kPanel(): boolean {
  try {
    const w = window.screen.width * (window.devicePixelRatio || 1);
    return w >= 3000;
  } catch {
    return false;
  }
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={S.row}>
      <span style={S.k}>{k}</span>
      <span style={S.v}>{v}</span>
    </div>
  );
}

function Note({ text }: { text?: string }) {
  if (!text) return null;
  return <div style={S.note}>{text}</div>;
}

/**
 * Inline styles on purpose: this page must not depend on styles.css, whose
 * modern features are exactly what may not exist here. Floats, margins and
 * pixels only - see rule 2 in the header. Do not "modernise" this.
 */
const S: Record<string, CSSProperties> = {
  page: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background: "#0b0e14",
    color: "#dfe6f2",
    fontFamily: "monospace",
    fontSize: 15,
    lineHeight: 1.4,
    padding: 12,
    boxSizing: "border-box",
  },
  header: {
    borderBottom: "2px solid #2b3450",
    paddingBottom: 6,
    marginBottom: 8,
    overflow: "hidden",
  },
  title: {
    fontSize: "1.3em",
    fontWeight: "bold",
    color: "#7fd4ff",
    float: "left",
  },
  headerRight: { fontSize: "1.05em", color: "#ffd479", float: "right" },
  cols: { overflow: "hidden" },
  colWide: {
    width: "72%",
    float: "left",
    paddingRight: 10,
    boxSizing: "border-box",
  },
  colNarrow: { width: "28%", float: "left", boxSizing: "border-box" },
  section: {
    border: "1px solid #232c44",
    borderRadius: 4,
    padding: "6px 8px",
    marginBottom: 8,
  },
  sectionTitle: {
    color: "#7fd4ff",
    fontWeight: "bold",
    fontSize: "0.95em",
    letterSpacing: 1,
    marginBottom: 4,
    borderBottom: "1px solid #232c44",
    paddingBottom: 2,
  },
  verdict: {
    border: "2px solid #7fd4ff",
    borderRadius: 4,
    padding: "8px 10px",
    marginBottom: 10,
    background: "#101726",
  },
  verdictLine: {
    fontSize: "1.05em",
    color: "#ffffff",
    marginBottom: 3,
    wordBreak: "break-word",
  },
  row: { overflow: "hidden", borderBottom: "1px dotted #1b2236" },
  k: { color: "#8b98b4", float: "left", marginRight: 8 },
  v: {
    color: "#dfe6f2",
    float: "right",
    textAlign: "right",
    wordBreak: "break-all",
  },
  note: { color: "#9fb0d0", fontSize: "0.85em", padding: "2px 0 4px 0" },
  guest: { color: "#7d8aa8", fontSize: "0.8em" },
  warn: {
    color: "#ffd479",
    fontSize: "0.85em",
    padding: "2px 0 4px 0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 4,
    fontSize: "0.85em",
  },
  th: {
    color: "#8b98b4",
    textAlign: "left",
    borderBottom: "1px solid #232c44",
    padding: "2px 4px",
    fontWeight: "normal",
  },
  td: {
    borderBottom: "1px dotted #1b2236",
    padding: "2px 4px",
    verticalAlign: "top",
    wordBreak: "break-word",
  },
  tdYes: {
    borderBottom: "1px dotted #1b2236",
    padding: "2px 4px",
    verticalAlign: "top",
    color: "#7ee787",
    fontWeight: "bold",
  },
  tdNo: {
    borderBottom: "1px dotted #1b2236",
    padding: "2px 4px",
    verticalAlign: "top",
    color: "#ff7b72",
    fontWeight: "bold",
  },
  trHit: { background: "#1d2740" },
  bannerLive: {
    background: "#0f2a17",
    border: "1px solid #2f7d4f",
    color: "#8fe0ac",
    padding: "6px 10px",
    marginTop: 6,
    fontSize: "0.86em",
  },
  bannerDead: {
    background: "#2a1414",
    border: "1px solid #7d3030",
    color: "#f0a0a0",
    padding: "6px 10px",
    marginBottom: 8,
    fontSize: "0.86em",
  },
  bannerNeutral: {
    background: "#141c2e",
    border: "1px solid #2b3450",
    color: "#cfe0ff",
    padding: "6px 10px",
    marginTop: 6,
    fontSize: "0.86em",
  },
  hint: { color: "#5f6b85", fontStyle: "italic" },
  json: {
    width: "100%",
    height: 220,
    background: "#070a10",
    color: "#9fb0d0",
    border: "1px solid #232c44",
    fontFamily: "monospace",
    fontSize: 11,
    boxSizing: "border-box",
  },
  video: {
    width: "100%",
    height: 120,
    background: "#000",
    border: "1px solid #2b3450",
  },
  footer: {
    clear: "both",
    marginTop: 6,
    paddingTop: 6,
    borderTop: "1px solid #232c44",
    color: "#5f6b85",
    fontSize: "0.86em",
  },
};
